// src/admin/pages/AdminDashboard.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { businessDashboardAPI } from "../services/businessDashboardService";

import StatCard from "../components/StatCard";
import { fmtPeso } from "../../utils/formatters";

import { Boxes, ShoppingBag, AlertTriangle, PhilippinePeso } from "lucide-react";
import ClockInWidget from "../components/erp/ClockInWidget";
import StorefrontDeliveryWarning from "../components/StorefrontDeliveryWarning";
import { PageHeader } from "../../components/ui";


function StatusPill({ status }) {
  const map = {
    in: {
      label: "In Stock",
      cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200",
    },
    low: {
      label: "Low Stock",
      cls: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200",
    },
    out: {
      label: "Out of Stock",
      cls: "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200",
    },
  };

  const v = map[status] || {
    label: "Unknown",
    cls: "bg-neutral-100 text-neutral-700 dark:bg-neutral-900 dark:text-neutral-200",
  };

  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${v.cls}`}>
      {v.label}
    </span>
  );
}

function ProgressRow({ label, value }) {
  const safe = Math.max(0, Math.min(100, Number(value || 0)));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm text-mnla-muted dark:text-mnla-darkMuted">
        <span>{label}</span>
        <span className="font-medium text-mnla-text dark:text-mnla-darkText">{safe}%</span>
      </div>

      <div className="h-2.5 rounded-full bg-neutral-200 dark:bg-neutral-800 overflow-hidden">
        <div className="h-full bg-mnla-accent" style={{ width: `${safe}%` }} />
      </div>
    </div>
  );
}

function TopSellingGraph({ items }) {
  const max = Math.max(...(items?.map((i) => Number(i.qty || 0)) || [1]));

  return (
    <div className="space-y-3">
      {(items || []).map((it, idx) => {
        const qty = Number(it.qty || 0);
        const pct = max ? Math.round((qty / max) * 100) : 0;

        return (
          <div key={`${it?.name || "item"}-${idx}`} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-mnla-text dark:text-mnla-darkText truncate pr-2">
                {it?.name || "—"}
              </span>
              <span className="text-mnla-muted dark:text-mnla-darkMuted tabular-nums">{qty}</span>
            </div>

            <div className="h-2.5 rounded-full bg-neutral-200 dark:bg-neutral-800 overflow-hidden">
              <div className="h-full bg-mnla-accent" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SkeletonLine({ className = "" }) {
  return (
    <div
      className={`animate-pulse rounded-lg bg-neutral-200 dark:bg-neutral-800 ${className}`}
      aria-hidden="true"
    />
  );
}

export default function BusinessDashboard() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [summary, setSummary] = useState({
    totalProducts: 0,
    ordersToday: 0,
    lowStockItems: 0,
    revenueToday: 0,
  });

  // expected: [{ name, category, price, stock, status: "in"|"low"|"out" }]
  const [recentProducts, setRecentProducts] = useState([]);

  // expected: { inStockPct, lowStockPct, outOfStockPct }
  const [health, setHealth] = useState({
    inStockPct: 0,
    lowStockPct: 0,
    outOfStockPct: 0,
  });

  // expected: [{ name, qty }]
  const [topSelling, setTopSelling] = useState([]);

  const statCards = useMemo(() => {
    return [
      {
        title: "Total Products",
        value: loading ? "—" : Number(summary.totalProducts || 0).toLocaleString(),
        icon: Boxes,
        to: "/business/products",
      },
      {
        title: "Orders Today",
        value: loading ? "—" : Number(summary.ordersToday || 0).toLocaleString(),
        icon: ShoppingBag,
        to: "/business/orders",
      },
      {
        title: "Low Stock Items",
        value: loading ? "—" : Number(summary.lowStockItems || 0).toLocaleString(),
        icon: AlertTriangle,
        to: "/business/products?stock=low",
        accent: true,
        hint: "Needs restock",
      },
      {
        title: "Revenue Today",
        value: loading ? "—" : `₱${Number(summary.revenueToday || 0).toLocaleString()}`,
        icon: PhilippinePeso,
        to: "/business/analytics",
      },
    ];
  }, [summary, loading]);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [sRes, rpRes, hRes, tsRes] = await Promise.all([
        businessDashboardAPI.summary(),
        businessDashboardAPI.recentProducts(),
        businessDashboardAPI.inventoryHealth(),
        businessDashboardAPI.topSelling(),
      ]);

      const s = sRes?.data;
      const rp = rpRes?.data;
      const h = hRes?.data;
      const ts = tsRes?.data;

      setSummary({
        totalProducts: s?.totalProducts ?? 0,
        ordersToday: s?.ordersToday ?? 0,
        lowStockItems: s?.lowStockItems ?? 0,
        revenueToday: s?.revenueToday ?? 0,
      });

      setRecentProducts(Array.isArray(rp) ? rp : rp?.items || []);

      setHealth({
        inStockPct: h?.inStockPct ?? 0,
        lowStockPct: h?.lowStockPct ?? 0,
        outOfStockPct: h?.outOfStockPct ?? 0,
      });

      setTopSelling(Array.isArray(ts) ? ts : ts?.items || []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load dashboard data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const goProducts = () => navigate("/business/products");

  return (
    <div className="space-y-6">
      {/* The business home: titled for what it is (audit §8), not "Inventory Dashboard". */}
      <PageHeader title="Dashboard" description="Monitor stock, sales performance, and daily activity." />

      <StorefrontDeliveryWarning />

      {/* ERROR */}
      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-200">
          {error}
        </div>
      ) : null}

      {/* STAT CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((c) => (
          <StatCard key={c.title} title={c.title} value={c.value} />
        ))}
      </div>

      {/* GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* RECENT PRODUCTS */}
        <div className="lg:col-span-2 rounded-2xl border border-mnla-border bg-mnla-card shadow-sm dark:border-mnla-darkBorder dark:bg-mnla-darkCard">
          <div className="flex items-center justify-between p-4 border-b border-mnla-border dark:border-mnla-darkBorder">
            <h2 className="font-semibold text-mnla-text dark:text-mnla-darkText">
              Recent Products
            </h2>

            <button
              type="button"
              onClick={goProducts}
              className="business-primary-action rounded-xl bg-mnla-accent px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
              View All
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-mnla-border dark:border-mnla-darkBorder">
                <tr className="text-left text-mnla-muted dark:text-mnla-darkMuted">
                  <th className="p-4 font-semibold">Product</th>
                  <th className="font-semibold">Category</th>
                  <th className="font-semibold">Price</th>
                  <th className="font-semibold">Stock</th>
                  <th className="pr-4 font-semibold">Status</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr
                      key={`sk-${i}`}
                      className="border-b border-mnla-border last:border-0 dark:border-mnla-darkBorder"
                    >
                      <td className="p-4">
                        <SkeletonLine className="h-4 w-40" />
                      </td>
                      <td>
                        <SkeletonLine className="h-4 w-24" />
                      </td>
                      <td>
                        <SkeletonLine className="h-4 w-20" />
                      </td>
                      <td>
                        <SkeletonLine className="h-4 w-16" />
                      </td>
                      <td className="pr-4">
                        <SkeletonLine className="h-6 w-24 rounded-full" />
                      </td>
                    </tr>
                  ))
                ) : (recentProducts || []).length ? (
                  (recentProducts || []).map((p, i) => {
                    const out = p?.status === "out";
                    return (
                      <tr
                        key={`${p?.name || "p"}-${i}`}
                        className="border-b border-mnla-border last:border-0 dark:border-mnla-darkBorder"
                      >
                        <td className="p-4 font-semibold text-mnla-text dark:text-mnla-darkText">
                          {p?.name || "—"}
                        </td>
                        <td className="text-mnla-muted dark:text-mnla-darkMuted">
                          {p?.category || "—"}
                        </td>
                        <td className="text-mnla-muted dark:text-mnla-darkMuted">
                          {p?.base_price_cents != null
                            ? fmtPeso(p.base_price_cents)
                            : "—"}
                        </td>
                        <td
                          className={`tabular-nums ${
                            out
                              ? "font-semibold text-rose-700 dark:text-rose-200"
                              : "text-mnla-muted dark:text-mnla-darkMuted"
                          }`}
                        >
                          {p?.stock ?? "—"}
                        </td>
                        <td className="pr-4">
                          <StatusPill status={p?.status} />
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td className="p-4 text-mnla-muted dark:text-mnla-darkMuted" colSpan={5}>
                      No recent products yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="space-y-6">
          {/* CLOCK-IN WIDGET */}
          <ClockInWidget />

          {/* INVENTORY HEALTH */}
          <div className="rounded-2xl border border-mnla-border bg-mnla-card shadow-sm p-4 dark:border-mnla-darkBorder dark:bg-mnla-darkCard">
            <h2 className="font-semibold mb-4 text-mnla-text dark:text-mnla-darkText">
              Inventory Health
            </h2>

            {loading ? (
              <div className="space-y-3">
                <SkeletonLine className="h-4 w-full" />
                <SkeletonLine className="h-4 w-11/12" />
                <SkeletonLine className="h-4 w-10/12" />
              </div>
            ) : (
              <div className="space-y-4">
                <ProgressRow label="In Stock Items" value={health.inStockPct} />
                <ProgressRow label="Low Stock Items" value={health.lowStockPct} />
                <ProgressRow label="Out of Stock" value={health.outOfStockPct} />
              </div>
            )}
          </div>

          {/* TOP SELLING */}
          <div className="rounded-2xl border border-mnla-border bg-mnla-card shadow-sm p-4 dark:border-mnla-darkBorder dark:bg-mnla-darkCard">
            <h2 className="font-semibold mb-4 text-mnla-text dark:text-mnla-darkText">
              Top Selling Products
            </h2>

            {loading ? (
              <div className="space-y-3">
                <SkeletonLine className="h-4 w-full" />
                <SkeletonLine className="h-4 w-11/12" />
                <SkeletonLine className="h-4 w-10/12" />
                <SkeletonLine className="h-4 w-9/12" />
              </div>
            ) : (topSelling || []).length ? (
              <TopSellingGraph items={topSelling} />
            ) : (
              <div className="text-sm text-mnla-muted dark:text-mnla-darkMuted">
                No sales data yet.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
